import React, { useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useLanguage, useProvider, useUser } from '~/Hooks';
import { FilterCheckboxList } from '~/Components/Filter';
import { useSelectedMap } from '../useSelectedMap';
import { FilterType } from '~/Hooks/useFilter';
import { useProviderFilters } from '~/Hooks/useFilter/useProviderFilters';
import { requestEventGroupList, selectEventGroupsList } from '~/store/eventGroups';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { selectActiveTrainerList } from '~/store/provider';
import { UserRole } from '~/Models/DTO/Role.model';

export interface IUseActiveEventsFilterProps {
  filterType: FilterType;
  initialFilter?: string[];
}

export const useActiveEventsFilter = ({
  filterType,
  initialFilter = [],
}: IUseActiveEventsFilterProps) => {
  const { appliedFilters } = useProviderFilters({ filterType });
  const { locationIds: initialState = initialFilter } = appliedFilters;
  const { currentUser, role } = useUser();
  const { t } = useLanguage();
  const dispatch = useDispatch();

  const { currentProvider } = useProvider();
  const getEventGroups = bindActionCreators(requestEventGroupList, dispatch);
  const eventGroups = useSelector(selectEventGroupsList);
  const trainers = useSelector(selectActiveTrainerList);

  const list = useMemo(
    () => eventGroups.map(({ id, name: label }: EventGroup) => ({ id, label })),
    [t, eventGroups],
  );
  const {
    selectItem,
    onClearMap,
    onApplyMap,
    selectedMap,
    selectedCount,
    updateSelectedMap,
    appliedSelectionIds,
  } = useSelectedMap({ initialState });

  useEffect(() => {
    if (currentProvider?.uuid && !eventGroups.length) {
      const trainerIds =
        role === UserRole.TRAINER
          ? trainers
              .filter((trainer) => trainer.userId === currentUser?.uuid)
              ?.map((trainer) => trainer.trainerId)
          : undefined;

      getEventGroups({
        trainerIds,
        providerId: currentProvider?.uuid,
        archived: false,
        public: true,
      });
    }
  }, [currentProvider?.uuid]);

  return {
    name: 'eventIds',
    title: t('app.checkin.table.eventName'),
    elements: <FilterCheckboxList list={list} selectItem={selectItem} selectedMap={selectedMap} />,
    onClearMap,
    onApplyMap,
    selectedCount,
    updateSelectedMap,
    appliedSelectionIds,
  };
};
